import React, {useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {Formik} from 'formik';
import {isEqual} from 'lodash';
import {useDispatch} from 'react-redux';
import moment from 'moment';
import {useUsersActionsContext} from '../context/UsersActionsContext';
import FilterTextField from '../../_common/components/FilterTextField';
import FilterSelect from '../../_common/components/FilterSelect';
import {FilterFromDate, FilterToDate} from '../../_common/components/FilterDateRange';
import FiltersBar from '../../_common/components/FiltersBar';
import ButtonExportToExcel from '../../_common/components/ButtonExportToExcel';
import * as actions from '../_redux/usersActionsActions';
import {RequestCancellationManager} from '../../_common/utils/RequestCancellationManager';
import {generateExcelFile} from '../../_common/utils/excel';
import {usersActionsHeader} from '../../_common/constants/exportedFilesMainHeaders';
import {headersUsersActions} from '../../_common/constants/exportedFilesHeaders';
import {actionTypes as T} from "../../_common/constants/actionTypes";
import {results as R} from "../../_common/constants/results";

const prepareFilter = (queryParams, values) => {
  const {
    username,
    fromDate,
    toDate,
    action,
    result
  } = values;
  const newQueryParams = {...queryParams};
  const filter = {};
  if (Boolean(username)) {
    filter.username = username;
  } else {
    delete newQueryParams.username;
  }
  if (Boolean(fromDate)) {
    filter.fromDate = moment(fromDate).format('YYYY-MM-DD');
  } else {
    delete newQueryParams.fromDate;
  }
  if (Boolean(toDate)) {
    filter.toDate = moment(toDate).add(1, 'days').format('YYYY-MM-DD');
  } else {
    delete newQueryParams.toDate;
  }
  if (Boolean(action)) {
    filter.action = action;
  } else {
    delete newQueryParams.action;
  }
  if (Boolean(result)) {
    filter.result = result;
  } else {
    delete newQueryParams.result;
  }

  return {...newQueryParams, ...filter};
};

export function UsersActionsFilter() {
  const dispatch = useDispatch();
  const intl = useIntl();
  const usersActionsContext = useUsersActionsContext();
  const usersActionsProps = useMemo(() => {
    return {
      queryParams: usersActionsContext.queryParams,
      setQueryParams: usersActionsContext.setQueryParams,
    };
  }, [usersActionsContext]);

  const applyFilter = (values) => {
    RequestCancellationManager.stopRequests(actions.getUsersActionsListIDPrefix);

    const newQueryParams = prepareFilter(usersActionsProps.queryParams, values);
    if (!isEqual(newQueryParams, usersActionsProps.queryParams)) {
      usersActionsProps.setQueryParams(newQueryParams);
    }
  };

  const qs = new URLSearchParams(window.location.search);
  let username = qs.get('username') || '';
  const fromDate = qs.get('fromDate') || '';
  const toDate = Boolean(qs.get('toDate'))
    ? moment(qs.get('toDate')).subtract(1, 'days').toDate()
    : '';
  let action = qs.get('action') || '';
  let result = qs.get('result') || '';

  const [loading, setLoading] = useState(false);
  const handleExport = () => {
    setLoading(true);
    dispatch(actions.getUserActivitiesLogsForExcelFile(usersActionsProps.queryParams))
      .then(prepareDataForExcel)
      .then((data) => generateExcelFile(data, 'Users_actions.xlsx', usersActionsHeader))
      .catch((error) => {
        console.error(`Error: + ${error}`);
        setLoading(false);
      })
      .finally(() => setLoading(false));
  };
  const prepareDataForExcel = data => {
    return data.map((item, index) => {
      const {
        username,
        createdAt,
        page,
        action,
        payload,
        subject,
        result,
      } = item;

      return {
        [headersUsersActions[0]]: index + 1,
        [headersUsersActions[1]]: username,
        [headersUsersActions[2]]: Boolean(createdAt) ? moment(createdAt).format('DD.MM.YYYY HH:mm:ss') : '',
        [headersUsersActions[3]]: action,
        [headersUsersActions[4]]: page,
        [headersUsersActions[4]]: subject,
        [headersUsersActions[5]]: payload,
        [headersUsersActions[6]]: result,
      };
    });
  };

  const containerClassnames = 'w-200px position-relative my-1 px-3';

  const actionTypeOptions = [
    {label: T.CREATE, value: T.CREATE},
    {label: T.EDIT, value: T.EDIT},
    {label: T.UPLOAD, value: T.UPLOAD},
    {label: T.DELETE, value: T.DELETE},
  ];

  const actionResultsOptions = [
    {label: R.SUCCESS, value: R.SUCCESS},
    {label: R.FAIL, value: R.FAIL},
  ];

  return (
    <>
      <Formik
        initialValues={{
          username,
          fromDate,
          toDate,
          action,
          result,
        }}
        onSubmit={(values) => applyFilter(values)}
        enableReinitialize={true}
      >
        {({values, ...formikProps}) => (
          <FiltersBar
            filters={
              <div className={'d-flex flex-wrap justify-content-center align-items-center mb-0 w-100'}>
                <FilterTextField
                  fieldName={'username'}
                  placeholder={intl.formatMessage({id: 'users-actions-filter-username', defaultMessage: 'Username'})}
                  value={values.username}
                  containerStyles={containerClassnames}
                  {...formikProps}
                />
                <FilterFromDate
                  values={values}
                  containerStyles={containerClassnames}
                  {...formikProps}
                />
                <FilterToDate
                  values={values}
                  containerStyles={containerClassnames}
                  {...formikProps}
                />
                <FilterSelect
                  fieldName={'action'}
                  valueName={values.action}
                  options={actionTypeOptions}
                  placeholder={intl.formatMessage({id: 'users-actions-filter-action', defaultMessage: 'Action'})}
                  containerStyles={containerClassnames}
                  {...formikProps}
                />
                <FilterSelect
                  fieldName={'result'}
                  valueName={values.result}
                  options={actionResultsOptions}
                  placeholder={intl.formatMessage({id: 'users-actions-filter-result', defaultMessage: 'Result'})}
                  containerStyles={containerClassnames}
                  {...formikProps}
                />
              </div>
            }
            buttons={
              <ButtonExportToExcel
                loading={loading}
                exportFunc={handleExport}
              />
            }
          />
        )}
      </Formik>
    </>
  );
}
